import React, { useState } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { FaPhone, FaEnvelope, FaMapMarkerAlt, FaClock, FaPaperPlane, FaSpinner } from 'react-icons/fa';
import axios from 'axios';
import { useLanguage } from '../../contexts/LanguageContext'; // Assurez-vous que le chemin est correct

const Contact = () => {
  const { t } = useLanguage();
  const tc = t.contactPage; // Raccourci pour accéder aux traductions de la page contact

  // 1. État du formulaire
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '', 
    company: '',
    service: '',
    message: ''
  });

  // 2. États pour l'interface
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState({ type: '', message: '' });

  // 3. Gestion des changements d'input
  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  // 4. Envoi du formulaire vers Spring Boot
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus({ type: '', message: '' });

    try {
      const response = await axios.post('https://www.s2i.com.tn/api/contact/public', formData);
      
      if (response.status === 200 || response.status === 201) {
        setStatus({ 
          type: 'success', 
          message: tc.messages.success
        });
        // Réinitialiser le formulaire
        setFormData({ name: '', email: '', phone: '', company: '', service: '', message: '' });
      }
    } catch (error) {
      console.error('Erreur contact:', error);
      setStatus({ 
        type: 'danger', 
        message: tc.messages.error 
      });
    } finally {
      setLoading(false);
    }
  };

  const contactInfo = [
    {
      icon: <FaMapMarkerAlt className="text-primary" style={{ fontSize: '1.5rem' }} />,
      title: tc.info.address,
      content: tc.info.city,
      details: tc.info.area
    },
    {
      icon: <FaPhone className="text-primary" style={{ fontSize: '1.5rem' }} />,
      title: tc.info.phone,
      content: "+216 70 839 470",
      details: tc.info.phoneHours
    },
    {
      icon: <FaEnvelope className="text-primary" style={{ fontSize: '1.5rem' }} />,
      title: "Email",
      content: "contact@s2i.tn",
      details: tc.info.emailResponse
    },
    {
      icon: <FaClock className="text-primary" style={{ fontSize: '1.5rem' }} />,
      title: tc.info.hours,
      content: tc.info.weekDays,
      details: tc.info.satHours
    }
  ];

  return (
    <div style={{ paddingTop: '80px' }}>
      {/* Hero Section */}
      <section className="text-white py-5" style={{ background: 'linear-gradient(135deg, #004a99 0%, #002d5c 100%)' }}>
        <Container>
          <Row className="justify-content-center text-center">
            <Col lg={10}>
              <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} className="display-3 fw-bold mb-4">
                {tc.hero.title}
              </motion.h1>
              <p className="lead">{tc.hero.subtitle}</p>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Form & Info Section */}
      <section className="py-5">
        <Container>
          <Row className="g-5">
            <Col lg={8}>
              <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }}>
                <h2 className="display-6 fw-bold mb-4">{tc.form.title}</h2>
                
                {status.message && (
                  <Alert variant={status.type} dismissible onClose={() => setStatus({type:'', message:''})}>
                    {status.message}
                  </Alert>
                )}

                <Form onSubmit={handleSubmit} className="shadow-sm p-4 p-md-5 rounded bg-white border">
                  <Row className="g-3">
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label className="fw-bold">{tc.form.labels.name}</Form.Label>
                        <Form.Control type="text" name="name" required value={formData.name} onChange={handleChange} placeholder={tc.form.placeholders.name} />
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label className="fw-bold">{tc.form.labels.email}</Form.Label>
                        <Form.Control type="email" name="email" required value={formData.email} onChange={handleChange} placeholder={tc.form.placeholders.email} />
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label className="fw-bold">{tc.form.labels.phone}</Form.Label>
                        <Form.Control 
                          type="tel" 
                          name="phone" 
                          value={formData.phone} 
                          onChange={handleChange} 
                          placeholder={tc.form.placeholders.phone} 
                        />
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label className="fw-bold">{tc.form.labels.company}</Form.Label>
                        <Form.Control type="text" name="company" value={formData.company} onChange={handleChange} placeholder={tc.form.placeholders.company} />
                      </Form.Group>
                    </Col>

                    <Col md={12}>
                      <Form.Group className="mb-3">
                        <Form.Label className="fw-bold">{tc.form.labels.service}</Form.Label>
                        <Form.Select name="service" value={formData.service} onChange={handleChange}>
                          <option value="">{tc.form.serviceOptions.select}</option>
                          <option value="Cyber Sécurité">{tc.form.serviceOptions.cyber}</option>
                          <option value="Infrastructure Data Center">{tc.form.serviceOptions.infra}</option>
                          <option value="Réseau & WIFI">{tc.form.serviceOptions.network}</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>

                    <Col md={12}>
                      <Form.Group className="mb-4">
                        <Form.Label className="fw-bold">{tc.form.labels.message}</Form.Label>
                        <Form.Control as="textarea" rows={5} name="message" required value={formData.message} onChange={handleChange} placeholder={tc.form.placeholders.message} />
                      </Form.Group>
                    </Col>

                    <Col md={12}>
                      <Button type="submit" variant="primary" size="lg" className="w-100 fw-bold py-3" disabled={loading} style={{ backgroundColor: '#004a99', border: 'none' }}>
                        {loading ? <><FaSpinner className="spinner-border-sm me-2 animate-spin" /> {tc.form.btn.sending}</> : <><FaPaperPlane className="me-2" /> {tc.form.btn.send}</>}
                      </Button>
                    </Col>
                  </Row>
                </Form>
              </motion.div>
            </Col>

            {/* Infos de contact à droite */}
            <Col lg={4}>
              <Card className="border-0 shadow-sm mb-4 bg-primary text-white p-4">
                <h4 className="fw-bold mb-4">{tc.sidebar.title}</h4>
                <ul className="list-unstyled mb-0">
                  <li className="mb-3">✔ {tc.sidebar.item1}</li>
                  <li className="mb-3">✔ {tc.sidebar.item2}</li>
                  <li>✔ {tc.sidebar.item3}</li>
                </ul>
              </Card>
              <div className="rounded overflow-hidden shadow-sm border bg-light d-flex flex-column align-items-center justify-content-center" style={{ height: '300px' }}>
                <FaMapMarkerAlt size={40} className="text-primary mb-2" />
                <p className="text-dark fw-bold mb-0">S2i Tunisie</p>
                <small className="text-muted">{tc.info.city}, {tc.info.area}</small>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  );
};

export default Contact;